// @system — hydration error reporting for main.jsx's hydrateRoot().
//
// React calls `onRecoverableError` when the server HTML did not match the client render and it
// fell back to a client render. The page still works, but the Largest Contentful Paint is now
// the client's paint (the reason SSR exists — docs/SCALABILITY.md "SSR of the public routes"),
// so every occurrence is a regression to fix, never noise to hide.
//
// Reporting goes through `reportError()` — the same path an uncaught exception takes: Sentry's
// global handler picks it up when a DSN is configured, DevTools shows it, and Playwright sees a
// `pageerror` (the LCP probe and e2e specs assert on it). It is deliberately NOT console.error:
// TerserPlugin strips console.* from production bundles (webpack.config.mjs drop_console), so a
// console-based report would vanish exactly where it matters.

/**
 * An Error that may carry the original as `cause` (ES2022 Error.cause; the client lib target is
 * ES2020, so the property is declared here rather than assumed from lib.es2022).
 * @typedef {Error & { cause?: unknown }} HydrationError
 */

/**
 * Wrap React's recoverable-error payload so the component stack survives the report.
 * @param {unknown} error
 * @param {{ componentStack?: string } | undefined} [errorInfo]
 * @returns {HydrationError}
 */
export function describeHydrationError(error, errorInfo) {
  const base = error instanceof Error ? error : new Error(String(error))
  const stack = errorInfo?.componentStack
  if (!stack) return base
  /** @type {HydrationError} */
  const wrapped = new Error(`${base.message}\nComponent stack:${stack}`)
  wrapped.name = base.name
  wrapped.cause = base
  return wrapped
}

/**
 * `onRecoverableError` for hydrateRoot().
 * @param {unknown} error
 * @param {{ componentStack?: string } | undefined} [errorInfo]
 */
export function reportHydrationError(error, errorInfo) {
  const described = describeHydrationError(error, errorInfo)
  if (typeof reportError === 'function') {
    reportError(described)
  } else {
    console.error(described)
  }
}
